import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import OnboardingGate from "@/components/auth/OnboardingGate";
import { useProfile } from "@/lib/profile/useProfile";
import { useMentorProfile } from "@/lib/mentors/useMentorProfile";
import { getApp, APPS } from "@/lib/apps";

const splitCsv = (s: string) =>
  s
    .split(",")
    .map((x) => x.trim())
    .filter(Boolean);

const STATUS_LABEL: Record<string, string> = {
  pending: "Under review",
  approved: "Approved",
  rejected: "Not approved",
};

const MentorApplyInner = () => {
  const { profile, loading: pLoading } = useProfile();
  const { profile: mentor, check, loading, upsert } = useMentorProfile();

  const defaultApp = profile?.active_app ?? profile?.primary_app ?? APPS[0]?.id;
  const [app, setApp] = useState<string>(defaultApp ?? "");
  const [headline, setHeadline] = useState("");
  const [bio, setBio] = useState("");
  const [specialties, setSpecialties] = useState("");
  const [languages, setLanguages] = useState("");
  const [rate, setRate] = useState<string>("");
  const [active, setActive] = useState(true);
  const [badge, setBadge] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const [err, setErr] = useState<string | null>(null);

  // Hydrate from existing mentor profile
  useEffect(() => {
    if (!mentor) return;
    setApp(mentor.app);
    setHeadline(mentor.headline ?? "");
    setBio(mentor.bio ?? "");
    setSpecialties((mentor.specialties ?? []).join(", "));
    setLanguages((mentor.languages ?? []).join(", "));
    setRate(mentor.hourly_rate_cents ? String(mentor.hourly_rate_cents / 100) : "");
    setActive(mentor.active);
    setBadge(mentor.badge_visible);
  }, [mentor]);

  const meta = useMemo(() => (app ? getApp(app as any) : null), [app]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErr(null);
    setSaving(true);
    const cents = rate ? Math.round(parseFloat(rate) * 100) : null;
    const res = await upsert({
      app,
      headline: headline.trim() || null,
      bio: bio.trim() || null,
      specialties: splitCsv(specialties),
      languages: splitCsv(languages),
      hourly_rate_cents: cents,
      currency: "USD",
      active,
      badge_visible: badge,
    });
    setSaving(false);
    if ("error" in res && res.error) {
      setErr(res.error);
      return;
    }
    setSavedAt(Date.now());
  };

  if (pLoading || loading) {
    return (
      <main className="mx-auto max-w-2xl px-6 py-12 text-sm text-muted-foreground">
        Loading…
      </main>
    );
  }

  return (
    <main className={`mx-auto max-w-2xl px-6 py-10 ${meta?.themeClass ?? ""}`}>
      <header className="mb-8">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">
          Mentorship
        </p>
        <h1 className="mt-2 text-3xl md:text-4xl font-semibold tracking-tight">
          {mentor ? "Edit your mentor profile" : "Become a mentor"}
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Share your expertise. Mentors are reviewed before being listed publicly.
        </p>

        {check ? (
          <div className="mt-4 inline-flex items-center gap-2 rounded-full border border-border bg-muted/40 px-3 py-1 text-xs font-medium">
            <span
              className={`h-1.5 w-1.5 rounded-full ${
                check.status === "approved"
                  ? "bg-emerald-500"
                  : check.status === "rejected"
                    ? "bg-destructive"
                    : "bg-amber-500"
              }`}
            />
            Status: {STATUS_LABEL[check.status] ?? check.status}
          </div>
        ) : null}
      </header>

      <form onSubmit={handleSubmit} className="space-y-5">
        <div>
          <label className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            App
          </label>
          <select
            value={app}
            onChange={(e) => setApp(e.target.value)}
            className="mt-1 w-full rounded-lg border border-border bg-background p-2 text-sm"
            required
          >
            {APPS.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name} — {a.community}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Headline
          </label>
          <input
            value={headline}
            onChange={(e) => setHeadline(e.target.value)}
            placeholder="e.g. Career coach for queer creatives"
            maxLength={120}
            className="mt-1 w-full rounded-lg border border-border bg-background p-2 text-sm"
            required
          />
        </div>

        <div>
          <label className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Bio
          </label>
          <textarea
            value={bio}
            onChange={(e) => setBio(e.target.value)}
            rows={5}
            maxLength={1000}
            placeholder="What you offer, your background, who you've helped…"
            className="mt-1 w-full rounded-lg border border-border bg-background p-2 text-sm"
            required
          />
        </div>

        <div>
          <label className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Specialties (comma separated)
          </label>
          <input
            value={specialties}
            onChange={(e) => setSpecialties(e.target.value)}
            placeholder="career, coming out, transitions"
            className="mt-1 w-full rounded-lg border border-border bg-background p-2 text-sm"
          />
        </div>

        <div>
          <label className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Languages (comma separated)
          </label>
          <input
            value={languages}
            onChange={(e) => setLanguages(e.target.value)}
            placeholder="English, Spanish"
            className="mt-1 w-full rounded-lg border border-border bg-background p-2 text-sm"
          />
        </div>

        <div>
          <label className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Hourly rate (USD)
          </label>
          <input
            type="number"
            min={0}
            step={5}
            value={rate}
            onChange={(e) => setRate(e.target.value)}
            placeholder="0 for free"
            className="mt-1 w-full rounded-lg border border-border bg-background p-2 text-sm"
          />
        </div>

        <div className="flex flex-col gap-2 rounded-lg border border-border p-3">
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={active}
              onChange={(e) => setActive(e.target.checked)}
            />
            Accepting new mentees
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={badge}
              onChange={(e) => setBadge(e.target.checked)}
            />
            Show mentor badge on my public profile
          </label>
        </div>

        {err ? <p className="text-xs text-destructive">{err}</p> : null}
        {savedAt ? (
          <p className="text-xs text-emerald-600 dark:text-emerald-400">Saved.</p>
        ) : null}

        <div className="flex items-center justify-between gap-3 pt-2">
          <Link
            to="/mentors"
            className="text-xs text-muted-foreground hover:text-foreground"
          >
            ← Browse mentors
          </Link>
          <button
            type="submit"
            disabled={saving}
            className="rounded-full bg-foreground text-background px-5 py-2 text-sm font-medium hover:opacity-90 transition-opacity disabled:opacity-50"
          >
            {saving ? "Saving…" : mentor ? "Save changes" : "Submit application"}
          </button>
        </div>
      </form>
    </main>
  );
};

const MentorApply = () => (
  <div className="min-h-screen flex flex-col bg-background text-foreground">
    <SiteHeader />
    <OnboardingGate>
      <MentorApplyInner />
    </OnboardingGate>
    <SiteFooter />
  </div>
);

export default MentorApply;
